import React from 'react';
import i18n from 'i18next';
import { I18nextProvider, initReactI18next, useTranslation } from 'react-i18next';
import { pl, type PolishLanguagePack } from './pl';
import { en, type EnglishLanguagePack } from './en';

type Primitive = string | number | boolean | null | undefined;
type TranslationParams = Record<string, Primitive>;
export type SupportedLanguage = 'pl' | 'en';
export type LanguagePack = PolishLanguagePack | EnglishLanguagePack;

const LANGUAGE_STORAGE_KEY = 'qpro_connect_language';

interface I18nContextValue {
  language: SupportedLanguage;
  messages: LanguagePack;
  t: (key: string, params?: TranslationParams) => string;
  setLanguage: (language: SupportedLanguage) => void;
}

const messagesByLanguage = {
  pl,
  en,
} as const;

const resources = {
  pl: {
    translation: pl,
  },
  en: {
    translation: en,
  },
} as const;

function normalizeLanguage(language?: string | null): SupportedLanguage {
  return language === 'en' ? 'en' : 'pl';
}

function getPersistedLanguage(): SupportedLanguage {
  try {
    return normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return 'pl';
  }
}

export function getLanguagePack(language: SupportedLanguage = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language)): LanguagePack {
  return messagesByLanguage[language];
}

export function getCurrentLanguage(): SupportedLanguage {
  return normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);
}

export function getCurrentLocale(): string {
  return getCurrentLanguage() === 'en' ? 'en-US' : 'pl-PL';
}

export function setAppLanguage(language: SupportedLanguage): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Ignore storage failures and still change runtime language.
  }

  void i18n.changeLanguage(language);
}

if (!i18n.isInitialized) {
  void i18n.use(initReactI18next).init({
    resources,
    lng: getPersistedLanguage(),
    fallbackLng: 'pl',
    supportedLngs: ['pl', 'en'],
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}

export function useI18n(): I18nContextValue {
  const { t, i18n: i18nextInstance } = useTranslation();
  const language = normalizeLanguage(i18nextInstance.resolvedLanguage ?? i18nextInstance.language);

  return React.useMemo(
    () => ({
      language,
      messages: getLanguagePack(language),
      t: (key, params) => t(key, params),
      setLanguage: setAppLanguage,
    }),
    [language, t]
  );
}

export { i18n, pl };
export type { EnglishLanguagePack, PolishLanguagePack };
